import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import ConsultantDialog, { QUOTE_LABEL, SECTOR_LABEL } from "@/components/consultants/ConsultantDialog";
import DraftEmailDialog from "@/components/consultants/DraftEmailDialog";
import { Search, Plus, Upload, Star, Pencil, Trash2, Mail, Phone, Briefcase } from "lucide-react";

export default function Consultants() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [meta, setMeta] = useState({ disciplines: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("all");
  const [sector, setSector] = useState("all");
  const [prefOnly, setPrefOnly] = useState(false);
  const [dialog, setDialog] = useState({ open: false, consultant: null });
  const [emailConsultant, setEmailConsultant] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => { api.get("/consultants/meta").then((r) => setMeta(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (discipline !== "all") params.discipline = discipline;
    if (sector !== "all") params.sector = sector;
    if (prefOnly) params.preferred_only = true;
    const r = await api.get("/consultants", { params });
    setRows(r.data); setLoading(false);
  }, [q, discipline, sector, prefOnly]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const removeConsultant = async (id) => {
    try { await api.delete(`/consultants/${id}`); toast.success("Consultant deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const onImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      const { data } = await api.post("/consultants/import", { rows: parsed });
      toast.success(`Imported ${data.inserted} consultants (${data.skipped} skipped)`);
      load();
    } catch (e) { toast.error("Import failed — check the CSV format"); }
    finally { setImporting(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((c) => {
      const key = (c.disciplines && c.disciplines[0]) || "Other";
      (g[key] = g[key] || []).push(c);
    });
    return g;
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px]">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-sm bg-zinc-900 text-white flex items-center justify-center shrink-0"><Briefcase className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Consultants</h1>
            <p className="text-zinc-500 mt-1 text-sm">{rows.length} in the firm-wide directory</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 h-10 px-3 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-600 hover:border-zinc-400 cursor-pointer transition-colors" data-testid="import-csv-button">
            <Upload className="h-4 w-4" /> {importing ? "Importing…" : "Import CSV"}
            <input type="file" accept=".csv" className="hidden" disabled={importing}
              onChange={(e) => { onImportFile(e.target.files[0]); e.target.value = ""; }} />
          </label>
          <Button data-testid="add-consultant-button" onClick={() => setDialog({ open: true, consultant: null })} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, contact, email…"
            className="pl-9 rounded-sm" data-testid="consultant-search" />
        </div>
        <Select value={discipline} onValueChange={setDiscipline}>
          <SelectTrigger className="w-auto min-w-[160px] rounded-sm" data-testid="discipline-filter"><SelectValue placeholder="All disciplines" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All disciplines</SelectItem>
            {meta.disciplines.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sector} onValueChange={setSector}>
          <SelectTrigger className="w-auto min-w-[120px] rounded-sm" data-testid="sector-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sectors</SelectItem>
            {Object.entries(SECTOR_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => setPrefOnly((v) => !v)} data-testid="preferred-toggle"
          className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-sm border text-sm transition-colors ${prefOnly ? "border-amber-400 bg-amber-50 text-amber-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
          <Star className={`h-4 w-4 ${prefOnly ? "fill-amber-400 text-amber-400" : ""}`} /> Preferred only
        </button>
      </div>

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : rows.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center rounded-sm">
          <Briefcase className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
          <h3 className="font-heading text-xl font-bold mb-1">No consultants found</h3>
          <p className="text-zinc-500 text-sm">Add one or import your register to get started.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.keys(grouped).map((disc) => (
            <section key={disc} data-testid={`discipline-group-${disc}`}>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">{disc}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {grouped[disc].map((c) => (
                  <div key={c.consultant_id} data-testid={`consultant-card-${c.consultant_id}`}
                    className="group bg-white border border-zinc-200 rounded-sm p-4 hover:border-zinc-300 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-heading font-bold text-base leading-snug flex items-center gap-1.5">
                          {c.preferred === "preferred" && <Star className="h-4 w-4 fill-amber-400 text-amber-400 shrink-0" data-testid={`pref-star-${c.consultant_id}`} />}
                          <span className="truncate">{c.company}</span>
                        </p>
                        {c.contacts && <p className="text-xs text-zinc-500 mt-0.5 truncate">{c.contacts}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => setEmailConsultant(c)} data-testid={`email-consultant-${c.consultant_id}`}
                          disabled={!(c.emails || []).length} title="Draft email"
                          className="h-7 w-7 flex items-center justify-center rounded-sm text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-40 transition-colors"><Mail className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setDialog({ open: true, consultant: c })} data-testid={`edit-consultant-${c.consultant_id}`}
                          className="h-7 w-7 flex items-center justify-center rounded-sm text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                        {isAdmin && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`delete-consultant-${c.consultant_id}`} className="h-7 w-7 flex items-center justify-center rounded-sm text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-sm">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {c.company}?</AlertDialogTitle>
                                <AlertDialogDescription>This removes the consultant from the firm-wide directory.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                <AlertDialogAction className="rounded-sm bg-rose-600 hover:bg-rose-700" onClick={() => removeConsultant(c.consultant_id)}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                    {c.disciplines?.length > 1 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.disciplines.map((d) => <span key={d} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">{d}</span>)}
                      </div>
                    )}
                    <div className="mt-2.5 space-y-1 text-xs text-zinc-600">
                      {c.emails?.length > 0 && <p className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 text-zinc-400 shrink-0" />{c.emails.join("; ")}</p>}
                      {c.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-zinc-400 shrink-0" />{c.phone}</p>}
                      {c.typical_fee && <p className="text-zinc-500">Fee: {c.typical_fee}</p>}
                    </div>
                    <div className="flex items-center gap-2 mt-2.5 flex-wrap text-[10px]">
                      <span className="px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500">{SECTOR_LABEL[c.sector] || c.sector}</span>
                      <span className="px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500">{QUOTE_LABEL[c.quote_needed] || c.quote_needed}</span>
                      {c.jobs_used_on?.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700" data-testid={`jobs-count-${c.consultant_id}`}>
                          Used on {c.jobs_used_on.length} job{c.jobs_used_on.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <ConsultantDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        disciplines={meta.disciplines} consultant={dialog.consultant} onSaved={load} />
      <DraftEmailDialog open={!!emailConsultant} onOpenChange={(o) => !o && setEmailConsultant(null)}
        projectId={null} project={null}
        service={emailConsultant ? (emailConsultant.disciplines?.[0] || "service") : ""}
        consultant={emailConsultant} assignedEmail="" />
    </div>
  );
}

// Minimal CSV parser handling quoted fields; maps the TWP register columns.
function parseCsv(text) {
  const records = [];
  let row = [], field = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') inQ = false;
      else field += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      if (field !== "" || row.length) { row.push(field); records.push(row); row = []; field = ""; }
    } else field += ch;
  }
  if (field !== "" || row.length) { row.push(field); records.push(row); }
  if (!records.length) return [];
  const header = records[0].map((h) => h.trim().toLowerCase());
  const idx = (name) => header.findIndex((h) => h.includes(name));
  const di = idx("discipline"), ci = idx("company"), coni = idx("contact"), ei = idx("email"),
    pi = idx("phone"), fi = idx("fee"), pri = idx("preferred"), ni = idx("notes");
  return records.slice(1).filter((r) => (r[ci] || "").trim()).map((r) => ({
    discipline: r[di] || "", company: r[ci] || "", contact: r[coni] || "", email: r[ei] || "",
    phone: r[pi] || "", fee: fi >= 0 ? r[fi] || "" : "", preferred: pri >= 0 ? r[pri] || "" : "",
    notes: ni >= 0 ? r[ni] || "" : "",
  }));
}
